import React, {useEffect, useState} from 'react';
import {Button, Form, Input, message, Modal, Table, Typography} from 'antd';
import {ArrowLeftOutlined, PlusOutlined} from '@ant-design/icons';
import {useNavigate, useParams} from "react-router-dom";
import api from "../../../utils/api";

const {Column} = Table;

const layout = {
    labelCol: {span: 8},
    wrapperCol: {span: 16},
};

const {Title} = Typography;

const PatientsListPage = () => {
    const {userId} = useParams();
    const [patients, setPatients] = useState([]);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [confirmLoading, setConfirmLoading] = useState(false);
    const [form] = Form.useForm();

    const navigate = useNavigate();

    const fetchPatients = async () => {
        setLoading(true);
        try {
            const response = await api.get(`/patients/${userId}`);
            // The response has the format shown in your example
            setPatients(response.data);
        } catch (error) {
            console.error("Error fetching patients:", error);
            message.error('Failed to load patients');
        } finally {
            setLoading(false);
        }
    };

    const showModal = () => {
        form.resetFields();
        setOpen(true);
    };

    const handleOk = async () => {
        try {
            const values = await form.validateFields();
            onFinish(values);
        } catch (errorInfo) {
            console.log('Validation failed:', errorInfo);
        }
    };

    const handleCancel = () => {
        form.resetFields();
        setOpen(false);
    };

    const handleBack = () => {
        navigate(-1); // Navigate back to the previous page
    };

    const onFinish = async (values) => {
        setConfirmLoading(true);
        try {
            // Format the request as per your example
            const patientData = {
                doctorId: userId,
                firstName: values.firstName,
                lastName: values.lastName,
                email: values.email,
                phone: values.phone
            };

            await api.post('/patients', patientData);

            // Show success message
            message.success('Patient added successfully');

            // Refresh the patients list
            await fetchPatients();

            // Reset form and close modal
            form.resetFields();
            setOpen(false);
        } catch (error) {
            console.error("Error adding patient:", error);
            message.error('Failed to add patient');
        } finally {
            setConfirmLoading(false);
        }
    };

    useEffect(() => {
        fetchPatients();
    }, [userId]);

    return (
        <>
            <div className="h-screen overflow-auto w-full">
                <div className="mt-3 w-full cardContainer">
                    <div className="flex items-center mb-4">
                        <Button
                            icon={<ArrowLeftOutlined/>}
                            onClick={handleBack}
                            className="mr-4"
                        >
                            Back
                        </Button>
                    </div>

                    <div className="flex justify-between">
                        <h1 className="text-2xl font-bold">Patients</h1>
                        <Button type="primary" icon={<PlusOutlined/>} onClick={showModal}>Add Patient</Button>
                    </div>
                    <div className="flex gap-3 w-full mt-3 flex-col">
                        <Table
                            dataSource={patients}
                            rowKey="id"
                            loading={loading}
                            onRow={(record) => ({
                                onClick: () => navigate(`/patient-cabinet/${record.id}`),
                                style: {cursor: 'pointer'}
                            })}
                        >
                            <Column
                                title="First Name"
                                dataIndex="first_name"
                                key="first_name"
                                sorter={(a, b) => a.first_name.localeCompare(b.first_name)}
                            />
                            <Column
                                title="Last Name"
                                dataIndex="last_name"
                                key="last_name"
                                sorter={(a, b) => a.last_name.localeCompare(b.last_name)}
                            />
                            <Column title="Email" dataIndex="email" key="email"/>
                            <Column title="Phone" dataIndex="phone" key="phone"/>
                        </Table>
                    </div>
                </div>
            </div>

            <Modal
                title="Add New Patient"
                open={open}
                onOk={handleOk}
                confirmLoading={confirmLoading}
                onCancel={handleCancel}
            >
                <Form
                    {...layout}
                    form={form}
                    name="add-patient-form"
                    style={{maxWidth: 600}}
                >
                    <Form.Item
                        name="firstName"
                        label="First Name"
                        rules={[
                            {required: true, message: 'Please input the first name!'}
                        ]}
                    >
                        <Input/>
                    </Form.Item>
                    <Form.Item
                        name="lastName"
                        label="Last Name"
                        rules={[
                            {required: true, message: 'Please input the last name!'}
                        ]}
                    >
                        <Input/>
                    </Form.Item>
                    <Form.Item
                        name="email"
                        label="Email"
                        rules={[
                            {required: true, message: 'Please input an email address!'},
                            {type: 'email', message: 'Please enter a valid email address!'}
                        ]}
                    >
                        <Input/>
                    </Form.Item>
                    <Form.Item
                        name="phone"
                        label="Phone Number"
                        rules={[
                            {required: true, message: 'Please input a phone number!'}
                        ]}
                    >
                        <Input/>
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
};

export default PatientsListPage;
